import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { useAddOnRules } from '../../common/hooks/useAddOnRules.js';
import { useAddOnVisibility } from '../../common/hooks/useAddOnVisibility.js';
import { additionalServicesMsg, invalidValueMsg, t } from '../../common/i18n/index.js';
import { isFeatureEnabled } from '../../common/utils/featureFlagUtils';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useFeatureFlags } from '../../public/site/ConfigProvider.js';
import { separatelyHandledFixedBroadbandAddOnCodes } from '../FixedBroadbandProductSelection/fixedBroadbandAddOnUtils.js';
import { Loading } from '../Loading/index.js';
import { AddOnExternalLink } from '../SubscriptionAdditionalService/AddOnExternalLink.js';
import { AddOnRow } from '../SubscriptionDetails/AddOnRow.js';
import {
  calculateBaseDependency,
  checkCurrentCombination,
  filterMobileReachabilityAddon,
  filterVisibilitiesBasedOnRing,
  getAddOnFromRules,
  getBaseRules,
  getDefaultSelectedLanguage,
  isMobileVoiceAddOn,
  updateEditabilityForZeroPriceAddOns,
} from '../SubscriptionDetails/addOnDependencyUtils.js';
import type { AddOnVisibilityOperationType } from '../../common/enums.js';
import type { AddedAddon } from '../../common/types/addon.js';
import type { AddOnVisibility } from '../../generated/api/addOnVisibility.js';
import type { CommercialProduct } from '../../generated/api/commercialProduct.js';
import type { SubscriptionAction } from '../../generated/api/subscriptionAction.js';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { BaseRules } from '../SubscriptionDetails/addOnDependencyUtils.js';

import './OrderSubscriptionConfiguration.scss';

interface AdditionalServicesCheckoutContentProps {
  subscriptionActions?: SubscriptionAction[];
  addOnAssociations?: AssociationRecord[];
  addOnDependencies?: DependencyRecord[];
  commercialProduct: CommercialProduct;
  isRingSelected?: boolean;
  cartItemInstanceId: string;
  productAddOns?: AddedAddon[];
  includedAddOnCodes?: string[];
  onAddOnSelectionChange?: (cartItemInstanceId: string, addedAddOns: AddedAddon[]) => void;
  showSeparator?: boolean;
  preFetchedAddOnVisibilities?: AddOnVisibility[];
  hideHeader?: boolean;
  loadNettiRules?: boolean;
  addOnVisibilityOperationType: AddOnVisibilityOperationType;
  isVakioSubscription?: boolean;
  hideDefaultAddons?: boolean;
}

interface AddOnCheckoutContentProps {
  baseRules: BaseRules;
  addOnVisibilities: AddOnVisibility[];
  cartItemInstanceId: string;
  productAddOnCodes: string[]; // Addons, that are not visible in the form
  preSelectedAddOnCodes: string[];
  initialLanguage?: string;
  onAddOnSelectionChange?: (cartItemInstanceId: string, addedAddOns: AddedAddon[]) => void;
}

const getCheckedAddOnCodes = (baseList: string[], currentItem: string, checked: boolean) => {
  return checked ? [...baseList, currentItem] : baseList.filter(item => item !== currentItem);
};

export const getAddOnItemsFromCodes = (
  addOnCodes: string[],
  addOnVisibilities: AddOnVisibility[],
  baseRules: BaseRules,
  selectedLanguage?: string
): AddedAddon[] =>
  addOnCodes.map(addOnCode => {
    const addOnItemFromVisibility = addOnVisibilities.find(visibility => visibility.addOnGuidCode === addOnCode)?.addOn;
    const addOnItemFromAssociation = getAddOnFromRules(
      addOnCode,
      baseRules.commercialProductCode,
      baseRules.addOnAssociations
    );
    const addOnItem = addOnItemFromVisibility || addOnItemFromAssociation;
    return {
      addOnCode: addOnCode,
      displayName: addOnItem?.addOnProductName || '',
      display: addOnItem?.display,
      addOnAssociationCode:
        baseRules.addOnAssociations
          .filter(assoc => assoc.cspmb__price_item__r.guid__c === baseRules.commercialProductCode)
          .find(assoc => assoc.cspmb__add_on_price_item__r.guid__c === addOnCode)?.guid__c || '',
      addOnAttributes: selectedLanguage && isMobileVoiceAddOn(addOnCode) ? { language: selectedLanguage } : undefined,
      monthlyPrice: addOnItem?.price?.effectivePrice.monthlyRecurringCharge,
      oneTimePrice: addOnItem?.price?.effectivePrice.oneTimeCharge,
    };
  });

const getCurrentAddonCodes = (
  subscriptionAddOnCodes: string[],
  addedAddOnCodes: string[],
  addOnVisibility: AddOnVisibility,
  checked: boolean
): string[] => {
  // Combine all addons that are currently included, non-editables and the ones that are selected in the form
  const combinedAddOnCodes = new Set([...subscriptionAddOnCodes, ...addedAddOnCodes]);
  // If the passed addon is checked, return all addons. If not, exclude it from the list.
  return checked
    ? Array.from(combinedAddOnCodes)
    : Array.from(combinedAddOnCodes).filter(addOnCode => addOnCode !== addOnVisibility.addOnGuidCode);
};

const AddOnCheckoutContent = ({
  baseRules,
  addOnVisibilities,
  cartItemInstanceId,
  preSelectedAddOnCodes,
  initialLanguage,
  onAddOnSelectionChange,
  productAddOnCodes,
}: AddOnCheckoutContentProps) => {
  const [addedAddOnCodes, setAddedAddOnCodes] = useState(preSelectedAddOnCodes);
  const [removedAddOnCodes, setRemovedAddOnCodes] = useState<string[]>([]);
  // All addon codes included in the subscription, visible and hidden. If addon can be controlled (checked/unchecked)
  // in the form, the result will be reflected to this array
  const [subscriptionAddOnCodes, setSubscriptionAddOnCodes] = useState<string[]>(
    Array.from(new Set([...preSelectedAddOnCodes, ...productAddOnCodes]))
  );
  const [editabilityRecords, setEditabilityRecords] = useState(
    calculateBaseDependency(baseRules, subscriptionAddOnCodes, addOnVisibilities, false, false)
  );
  const [isValidCombination, setIsValidCombination] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
  const { setValue, setError, clearErrors } = useFormContext();

  const formName = `configuration.${cartItemInstanceId}.addOns.addedAddOns`;

  useEffect(() => {
    const currentCombination = checkCurrentCombination(baseRules, [], [], subscriptionAddOnCodes, addOnVisibilities);

    if (currentCombination.isValid) {
      clearErrors(formName);
    } else {
      setError(formName, { type: 'custom', message: t.DUPA(invalidValueMsg) });
    }
    setIsValidCombination(currentCombination.isValid);
  }, [addOnVisibilities, addedAddOnCodes, baseRules, clearErrors, formName, setError, subscriptionAddOnCodes]);

  const handleRemovedAddOns = (addOnCode: string, checked: boolean) => {
    if (!checked && preSelectedAddOnCodes.includes(addOnCode)) {
      const uncheckedAddOnCodes = [...removedAddOnCodes, addOnCode];
      setValue(`configuration.${cartItemInstanceId}.addOns.removedAddOns`, uncheckedAddOnCodes);
      setRemovedAddOnCodes(uncheckedAddOnCodes);
    }
  };

  const handleLanguageChange = (language?: string) => {
    const addedAddOnItems = getAddOnItemsFromCodes(addedAddOnCodes, addOnVisibilities, baseRules, language);
    setValue(formName, addedAddOnItems);
    onAddOnSelectionChange?.(cartItemInstanceId, addedAddOnItems);
    setSelectedLanguage(language);
  };

  const handleAddOnSelection = (checked: boolean, visibility: AddOnVisibility) => {
    const checkedAddOnCodes = getCheckedAddOnCodes(addedAddOnCodes, visibility.addOnGuidCode, checked);
    const languageIfCheckedOrUnchecked = checked ? getDefaultSelectedLanguage() : undefined;
    const language = isMobileVoiceAddOn(visibility.addOnGuidCode) ? languageIfCheckedOrUnchecked : selectedLanguage;
    const currentAddOns = getCurrentAddonCodes(subscriptionAddOnCodes, checkedAddOnCodes, visibility, checked);
    const editability = calculateBaseDependency(baseRules, currentAddOns, addOnVisibilities, false, false);
    setSubscriptionAddOnCodes(currentAddOns);
    const addedAddOnItems = getAddOnItemsFromCodes(checkedAddOnCodes, addOnVisibilities, baseRules, language);
    setValue(formName, addedAddOnItems);
    onAddOnSelectionChange?.(cartItemInstanceId, addedAddOnItems);
    setSelectedLanguage(language);
    setAddedAddOnCodes(checkedAddOnCodes);
    setEditabilityRecords(editability);
    handleRemovedAddOns(visibility.addOnGuidCode, checked);
  };

  return (
    <Controller
      name={formName}
      defaultValue={
        initialLanguage
          ? getAddOnItemsFromCodes(addedAddOnCodes, addOnVisibilities, baseRules, initialLanguage)
          : undefined
      }
      rules={{ validate: () => (isValidCombination ? undefined : t.DUPA(invalidValueMsg)) }}
      shouldUnregister={true}
      render={({ fieldState: { error } }) => (
        <div className="of-order-product-details__addon-selections">
          {addOnVisibilities
            .filter(addOnVisibility => addOnVisibility.selfServiceEditable)
            .map((addOnVisibility, i) => (
              <AddOnRow
                key={`checkout-addon-${i}`}
                addOnVisibility={addOnVisibility}
                showEditView={true}
                editabilityRecords={editabilityRecords}
                subscriptionAddOnCodes={subscriptionAddOnCodes}
                addOnCodesToAdd={addedAddOnCodes}
                addOnCodesToRemove={[]}
                isAssetWithMobiiliturva={false}
                isEmployee={false}
                handleAddOnSelectionChange={(checked, visibility) => handleAddOnSelection(checked, visibility)}
                baseRules={baseRules}
                selectedLanguage={selectedLanguage}
                onChangeLanguage={(language?: string) => handleLanguageChange(language)}
              />
            ))}
          <AddOnExternalLink linkHref={paths.INSTRUCTIONS_SUBSCRIPTION_ADDONS} />
          {error && <div className={dsClass.INPUTERROR}>{error.message}</div>}
        </div>
      )}
    />
  );
};

export const AdditionalServicesCheckoutContent = ({
  commercialProduct,
  isRingSelected = false,
  cartItemInstanceId,
  productAddOns,
  includedAddOnCodes,
  onAddOnSelectionChange,
  showSeparator,
  preFetchedAddOnVisibilities,
  hideHeader,
  loadNettiRules,
  addOnVisibilityOperationType,
  addOnAssociations,
  addOnDependencies,
  isVakioSubscription = false,
  hideDefaultAddons = false,
}: AdditionalServicesCheckoutContentProps) => {
  // Only fetch rules from Redux if they weren't provided as props
  const shouldLoadRules = !addOnAssociations && !addOnDependencies;

  const fetchedRules = useAddOnRules(shouldLoadRules ? loadNettiRules : false, shouldLoadRules);

  // Use provided props if available, otherwise use fetched rules
  const associations = addOnAssociations ?? fetchedRules.associations;
  const dependencies = addOnDependencies ?? fetchedRules.dependencies;
  const addOnRulesLoading = shouldLoadRules && fetchedRules.addOnRulesLoading;

  const { showReachabilityWithMobile } = useFeatureFlags();

  // Anonymous users have no session; route their add-on visibility fetch to the public endpoint.
  const { authenticatedUser } = useAuth();

  const showMobileReachabilityAddon = isFeatureEnabled(showReachabilityWithMobile) && isVakioSubscription;

  const { addOnVisibilities, addOnVisibilitiesLoading } = useAddOnVisibility(
    commercialProduct.commercialProductCode,
    addOnVisibilityOperationType,
    preFetchedAddOnVisibilities,
    undefined,
    Boolean(authenticatedUser)
  );

  const isAllDataLoaded = !addOnVisibilitiesLoading && !addOnRulesLoading;

  if (!isAllDataLoaded) {
    return <Loading />;
  }

  const baseRules: BaseRules = getBaseRules(commercialProduct, associations, dependencies);

  const ordinaryAddOnVisibilities = addOnVisibilities?.filter(
    a => !separatelyHandledFixedBroadbandAddOnCodes.includes(a.addOnGuidCode)
  );
  const visibilitiesConsideringRing = filterVisibilitiesBasedOnRing(isRingSelected, ordinaryAddOnVisibilities);
  const visibilitiesConsideringMobileReachability = filterMobileReachabilityAddon(
    showMobileReachabilityAddon,
    visibilitiesConsideringRing
  );
  const visibilities = updateEditabilityForZeroPriceAddOns(
    hideDefaultAddons,
    visibilitiesConsideringMobileReachability
  ).filter(visibility => !includedAddOnCodes?.includes(visibility.addOnGuidCode));

  const productAddOnCodes =
    productAddOns
      ?.map(addOn => addOn.addOnCode)
      .filter(
        addOnCode => !visibilitiesConsideringRing.map(visibility => visibility.addOnGuidCode).includes(addOnCode)
      ) || [];
  const preSelectedAddOnCodes =
    productAddOns
      ?.map(addOn => addOn.addOnCode)
      .filter(addOnCode =>
        visibilitiesConsideringRing.map(visibility => visibility.addOnGuidCode).includes(addOnCode)
      ) || [];

  // Restore voicemail language from productAddOns if available, otherwise use default
  const storedLanguage = productAddOns?.find(
    addOn => isMobileVoiceAddOn(addOn.addOnCode) && addOn.addOnAttributes?.language
  )?.addOnAttributes?.language;

  const initialLanguage = preSelectedAddOnCodes?.some(code => isMobileVoiceAddOn(code))
    ? storedLanguage || getDefaultSelectedLanguage()
    : undefined;

  return (
    <>
      {visibilities.some(visibility => visibility.selfServiceEditable) && (
        <div>
          {!hideHeader && (
            <div className={dsClass.MARGIN_TOP_5}>
              {showSeparator && <hr className={`${dsClass.MARGIN_BOTTOM_5} ${dsClass.MARGIN_TOP_5}`} />}
              <div className={`${dsClass.H4} ${dsClass.MARGIN_BOTTOM_5}`}>{t.LXSR(additionalServicesMsg)}</div>
            </div>
          )}
          <AddOnCheckoutContent
            baseRules={baseRules}
            addOnVisibilities={visibilities}
            cartItemInstanceId={cartItemInstanceId}
            productAddOnCodes={productAddOnCodes}
            preSelectedAddOnCodes={preSelectedAddOnCodes}
            initialLanguage={initialLanguage}
            onAddOnSelectionChange={onAddOnSelectionChange}
          />
        </div>
      )}
    </>
  );
};
